import { format } from 'date-fns';
import type { SearchFormValues } from '@/components/forms/SearchFromProvider';
import { env } from '@/env';

/**
 * Query param on the current page that opts a booking link into the beta OBE origin
 * (`NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL`) instead of the default one. Accepts `1`, `true` or `yes`.
 */
const BETA_OBE_TOGGLE_PARAM = 'useBetaObe';
const BETA_OBE_TOGGLE_TRUTHY = new Set(['1', 'true', 'yes']);

export function shouldUseBetaObe(): boolean {
    if (typeof window === 'undefined' || !env.NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL) {
        return false;
    }
    const value = new URLSearchParams(window.location.search).get(BETA_OBE_TOGGLE_PARAM);
    return value !== null && BETA_OBE_TOGGLE_TRUTHY.has(value.toLowerCase());
}

export function getObeBaseUrl(): URL {
    const baseUrl =
        shouldUseBetaObe() && env.NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL
            ? env.NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL
            : env.NEXT_PUBLIC_JUCY_OBE_DIRECT_URL || 'https://bookings.jucy.com/obe/direct/trip/create/';
    return new URL(baseUrl);
}

function buildObeSearchParams(values: Partial<SearchFormValues>): URLSearchParams {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(values)) {
        if (!value) {
            continue;
        }
        if (['pickUpDate', 'dropOffDate'].includes(key) && value instanceof Date) {
            searchParams.append(key, format(value, 'yyyy-MM-dd'));
            continue;
        }
        if (['pickUpTime', 'dropOffTime'].includes(key) && value instanceof Date) {
            searchParams.append(key, format(value, 'HHmm'));
            continue;
        }
        if (key === 'dropOffBranch' && typeof value === 'string') {
            searchParams.append('dropOffLocation', value);
            continue;
        }
        if (key === 'pickUpBranch' && typeof value === 'string') {
            searchParams.append('pickUpLocation', value);
            continue;
        }
        searchParams.append(key, value.toString());
    }

    return searchParams;
}

export function buildObeSearchUrl(values: SearchFormValues, region = 'nz'): URL {
    const { fleetType, ...rest } = values;
    const params = buildObeSearchParams(rest);
    params.append('action', 'search');
    const path = `/obe/${region}/direct/trip/create/${fleetType}`;
    return buildObeLinkUrl({ path, params: Object.fromEntries(params) });
}

export function buildObeLinkUrl({ path, params }: { path: string; params?: Record<string, string> }): URL {
    const url = new URL(path, getObeBaseUrl());
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            url.searchParams.set(key, value);
        }
    }
    return url;
}
